import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError, type Product } from '../lib/api'
import { useAsync, useCart, useStore } from '../lib/store'
import { ErrorNote, Fill, Loading, Shell } from '../components/Layout'

type Result = { title: string; body: string; items: Product[]; total: number }

export default function Quiz() {
  const { data, loading, error, reload } = useAsync(() => api.quizQuestions(), [])
  const { addMany } = useCart()
  const { money } = useStore()

  const [step, setStep] = useState(-1)
  const [answers, setAnswers] = useState<number[]>([])
  const [result, setResult] = useState<Result | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [addedAll, setAddedAll] = useState(false)

  if (loading) return <Shell><Loading label="Loading quiz" /></Shell>
  if (error || !data) return <Shell><ErrorNote message={error ?? 'Could not load the quiz.'} onRetry={reload} /></Shell>

  const questions = data.questions
  const total = questions.length

  async function choose(index: number) {
    const next = answers.slice()
    next[step] = index
    setAnswers(next)

    if (step + 1 < total) {
      setStep(step + 1)
      return
    }

    // Last answer — the server resolves the routine from the real catalogue.
    setSubmitting(true)
    setSubmitError('')
    try {
      setResult(await api.submitQuiz(next))
      setStep(total)
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.message : 'Could not build your routine.')
    } finally {
      setSubmitting(false)
    }
  }

  function restart() {
    setStep(-1)
    setAnswers([])
    setResult(null)
    setAddedAll(false)
    setSubmitError('')
  }

  const isIntro = step === -1
  const isQuestion = step >= 0 && step < total
  const isResult = step >= total && result

  return (
    <Shell>
      <section className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', background: 'var(--blush)', minHeight: 'clamp(420px,44vw,580px)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 20, padding: 'clamp(28px,4.4vw,68px)' }}>
          {isIntro && (
            <>
              <div className="eyebrow-rose">The Lumera skin quiz</div>
              <h1 className="display h-hero" style={{ maxWidth: '16ch' }}>
                Sixty seconds to a routine that fits.
              </h1>
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 300, lineHeight: 1.8, color: '#4b3c3c', maxWidth: '38ch' }}>
                Five questions about how your skin actually behaves. We will match you to three Lumera products that work together —
                no upsell, no twelve-step routine.
              </p>
              <button className="btn btn-solid" style={{ alignSelf: 'flex-start', padding: '14px 30px' }} onClick={() => setStep(0)}>
                Start the quiz
              </button>
            </>
          )}

          {isQuestion && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="eyebrow-rose">
                  Question {step + 1} of {total}
                </div>
                <div style={{ height: 2, background: '#eddede', width: '100%', maxWidth: 320 }}>
                  <div style={{ height: '100%', background: 'var(--ink)', width: `${((step + 1) / total) * 100}%`, transition: 'width .3s ease' }} />
                </div>
              </div>

              <h2 key={step} className="display fade" style={{ fontSize: 'clamp(22px,2.7vw,36px)', maxWidth: '20ch' }}>
                {questions[step].t}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 420 }}>
                {questions[step].o.map((option, i) => {
                  const selected = answers[step] === i
                  return (
                    <button
                      key={option}
                      onClick={() => choose(i)}
                      disabled={submitting}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', cursor: 'pointer',
                        background: selected ? 'var(--blush-2)' : 'rgba(253,251,249,.7)',
                        border: `1px solid ${selected ? 'var(--ink)' : '#eddede'}`,
                        padding: '14px 16px', fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: 'var(--ink)',
                      }}
                    >
                      <span style={{ width: 9, height: 9, borderRadius: '50%', border: '1px solid var(--ink)', background: selected ? 'var(--ink)' : 'transparent', flex: '0 0 auto' }} />
                      {option}
                    </button>
                  )
                })}
              </div>

              {submitError && <div className="notice notice-error">{submitError}</div>}

              <button
                className="btn btn-sm"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setStep(step - 1)}
                disabled={submitting}
              >
                {submitting ? 'Building your routine…' : 'Back'}
              </button>
            </>
          )}

          {isResult && (
            <>
              <div className="eyebrow-rose">Your result</div>
              <h2 className="display h-lg" style={{ maxWidth: '16ch' }}>
                {result!.title}
              </h2>
              <p style={{ margin: 0, fontSize: 13.5, fontWeight: 300, lineHeight: 1.85, color: '#4b3c3c', maxWidth: '42ch' }}>{result!.body}</p>
              <button className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={restart}>
                Retake the quiz
              </button>
            </>
          )}
        </div>

        <div style={{ position: 'relative', minHeight: 'clamp(260px,30vw,580px)' }}>
          <Fill src="/assets/photos/applying-eyes-closed.jpg" alt="A woman applying moisturiser to her cheek, eyes closed, dappled leaf shadow behind" label="Cream swatch" />
        </div>
      </section>

      {isResult && (
        <section className="section">
          <h2 className="rule-head">Your three-step routine</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 'clamp(14px,2vw,26px)' }}>
            {result!.items.map((p, i) => (
              <article key={p.slug} className="card">
                <Link to={`/product/${p.slug}`} className="card-media" style={{ display: 'block' }}>
                  <Fill src={p.image} alt={p.name} label={p.name} />
                  <span className="badge">Step 0{i + 1}</span>
                </Link>
                <div className="card-body">
                  <div className="card-step">{p.step}</div>
                  <h3 className="card-name">
                    <Link to={`/product/${p.slug}`}>{p.name}</Link>
                  </h3>
                  <p className="card-blurb">{p.tagline}</p>
                  <div className="card-price">
                    {money(p.price)}
                    {p.size && ` · ${p.size}`}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginTop: 'clamp(26px,3vw,40px)' }}>
            <div style={{ fontSize: 13, letterSpacing: '.04em' }}>
              Routine total: <strong style={{ fontFamily: 'var(--serif)', fontSize: 20, fontWeight: 400 }}>{money(result!.total)}</strong>
            </div>
            <button
              className="btn btn-solid"
              style={{ padding: '14px 30px' }}
              disabled={addedAll}
              onClick={() => {
                addMany(
                  result!.items.map((p) => ({ slug: p.slug, qty: 1 })),
                  'Your routine'
                )
                setAddedAll(true)
              }}
            >
              {addedAll ? 'Added to your bag' : 'Add all three to bag'}
            </button>
            {addedAll && (
              <Link to="/cart" style={{ fontSize: 12, textDecoration: 'underline' }}>
                View your bag →
              </Link>
            )}
          </div>
        </section>
      )}
    </Shell>
  )
}
